import { useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { SelectFormField } from '@/components/form/select-form-field'
import { Typography } from '@/components/ui/typography'
import { DAY_NAMES_NARROW } from '@/constants'
import { generateTimeOptions } from '@/utils/date/generate-time-options'

import * as S from './market-shop-settings.styled'
import { MarketShopSettingsFormData } from './market-shop-settings.types'

export interface BusinessHourRowProps {
  disabled: boolean
  index: number
  onToggle: () => void
}

export const BusinessHourRow = ({ disabled, index, onToggle }: BusinessHourRowProps) => {
  const { t } = useTranslation(['market-shop-dashboard'])
  const startTime = useWatch<MarketShopSettingsFormData, `businessHours.${number}.startTime`>({
    name: `businessHours.${index}.startTime`,
  })
  const endTime = useWatch<MarketShopSettingsFormData, `businessHours.${number}.endTime`>({
    name: `businessHours.${index}.endTime`,
  })
  const isEnabled = useWatch<MarketShopSettingsFormData, `businessHours.${number}.enabled`>({
    name: `businessHours.${index}.enabled`,
  })

  const startTimeOptions = useMemo(() => {
    if (endTime == null) return generateTimeOptions()
    return generateTimeOptions().map((option) => ({
      ...option,
      disabled: option.value >= endTime,
    }))
  }, [endTime])

  const endTimeOptions = useMemo(() => {
    if (startTime == null) return generateTimeOptions()
    return generateTimeOptions().map((option) => ({
      ...option,
      disabled: option.value <= startTime,
    }))
  }, [startTime])

  return (
    <S.DayRow>
      <S.DayButton type="button" $isEnabled={!!isEnabled} disabled={disabled} onClick={onToggle}>
        {DAY_NAMES_NARROW[index]}
      </S.DayButton>
      {isEnabled ? (
        <S.TimeInputs>
          <SelectFormField
            name={`businessHours.${index}.startTime`}
            size="medium"
            placeholder={t('market-shop-dashboard:settings.businessHours.startTime.placeholder')}
            disabled={disabled}
            options={startTimeOptions}
          />
          <SelectFormField
            name={`businessHours.${index}.endTime`}
            size="medium"
            placeholder={t('market-shop-dashboard:settings.businessHours.endTime.placeholder')}
            disabled={disabled}
            options={endTimeOptions}
          />
        </S.TimeInputs>
      ) : (
        <S.UnavailableText>
          <Typography size="text-medium" color="black500">
            {t('market-shop-dashboard:settings.businessHours.unavailable')}
          </Typography>
        </S.UnavailableText>
      )}
    </S.DayRow>
  )
}
